import React, { useState, useEffect, useCallback } from "react";
import { useApi } from "../../api/useApi";
import { usersInEnabledWorkGroups } from "../../api/userQueries";
import { FormControl } from "../../components/Form/FormControls";
import Button from "../Button";
import ErrorMessages from "../../components/Notifications/ErrorMessages";
import { DateTimePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import PagedTable from "../../components/Table/PagedTable";
import { exportAuditLogMutation } from "../../api/auditMutations";
import TableButton from "../../components/Button/TableButton";
import { NotificationLoading } from "../../components/Notification/NotificationLoading";
import Notification from "../../components/Notification";
import { trackCustomEvent } from "../../appInsights";
import { useSearch } from "../../Hooks/useSearch";
import SearchInput from "../../components/Forms/Search/SearchInput";

const SearchUsers = ({ setSelected, selectedUser }) => {
  const [userData, setUserData] = useState([]);
  const [pageSize, setPageSize] = useState(50);

  const [
    { errors: getUserErrors, loading: loadingUsers, data: usersDataFetch },
    getUsers,
  ] = useApi();

  //Fetch for Table Paged
  const fetchData = useCallback(
    ({ pageSize: incomingPageSize, cursor }) => {
      const finalPageSize = incomingPageSize || pageSize;
      getUsers({
        query: usersInEnabledWorkGroups,
        variables: {
          first: finalPageSize,
          after: cursor,
          order: { displayName: "ASC" },
        },
      });
    },
    [getUsers, pageSize]
  );

  const onSearch = useCallback(
    (text) => {
      const where = {};
      if (text) {
        where.or = [{ displayName: { contains: text } }];
      }

      const variables = {
        order: [{ displayName: "ASC" }],
        ...(Object.keys(where).length > 0 && { where }),
        first: pageSize, // Always use current page size
      };

      getUsers({
        query: usersInEnabledWorkGroups,
        variables,
      });
    },
    [getUsers, pageSize]
  );

  const { inputText, handleInputChange, tooShortWarning } = useSearch({
    onSearch,
  });

  const totalCount = usersDataFetch?.usersInEnabledWorkGroups?.totalCount;
  const pageInfo = usersDataFetch?.usersInEnabledWorkGroups?.pageInfo;

  const columnsData = [
    {
      Header: "Name",
      id: "name",
      accessor: (d) => d?.node?.displayName,
    },
    {
      Header: "Email",
      id: "emailAddress",
      accessor: (d) => d?.node?.emailAddress,
      Cell: ({ row: { original } }) => {
        return (
          <div
            style={{
              maxWidth: "300px",
              textOverflow: "ellipsis",
              overflow: "hidden",
            }}
            title={original?.node?.emailAddress}
          >
            {original?.node?.emailAddress}
          </div>
        );
      },
    },
    {
      Header: () => (
        <div
          style={{
            minWidth: "165px",
            maxWidth: "165px",
          }}
        ></div>
      ),
      id: "Selection",
      Cell: ({ row: { original } }) => {
        const isSelected = selectedUser?.id === original?.node?.id;
        return (
          <div>
            <TableButton
              list="true"
              type="button"
              disabled={isSelected}
              onClick={() => setSelected(original?.node)}
            >
              Select
            </TableButton>
          </div>
        );
      },
    },
  ];

  useEffect(() => {
    if (usersDataFetch) {
      const availUsers = usersDataFetch?.usersInEnabledWorkGroups?.edges ?? [];
      setUserData(availUsers);
    }
  }, [usersDataFetch]);

  return (
    <>
      <FormControl style={{ maxWidth: "300px" }}>
        <SearchInput
          label="Search User"
          placeholder="Search User"
          inputText={inputText}
          handleChange={handleInputChange}
          warning={tooShortWarning}
        />

        {getUserErrors ? <ErrorMessages errors={getUserErrors} /> : null}
      </FormControl>

      <PagedTable
        fetchData={fetchData}
        loading={loadingUsers}
        pageInfo={pageInfo}
        totalCount={totalCount}
        data={userData}
        columns={columnsData}
        defaultPageSize={50}
        onPageSizeChange={(newSize) => setPageSize(newSize)}
      />
    </>
  );
};

function ExportAuditLog({ cancel }) {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const [endTime, setEndTime] = useState(today);
  const [startTime, setStartTime] = useState(yesterday);
  const [useDateRange, setUseDateRange] = useState(false);
  const [filterByUser, setFilterByUser] = useState(false);
  const [selectedUser, setSelectedUser] = useState();
  const [showExport, setShowExport] = useState(false);

  const resetExport = useCallback(() => {
    setShowExport(false);
    cancel();
  }, [setShowExport, cancel]);

  const [{ loading, errors, data }, createExport] = useApi();

  useEffect(() => {
    if (data) {
      setShowExport(true);
    }
  }, [data, setShowExport]);

  const callCreateExport = useCallback(() => {
    createExport({
      query: exportAuditLogMutation,
      variables: {
        ...(useDateRange && {
          startTime: startTime.toISOString(),
        }),
        ...(useDateRange && {
          endTime: endTime.toISOString(),
        }),
        ...(selectedUser?.id && {
          userFilter: selectedUser?.id,
        }),
      },
    });
  }, [useDateRange, selectedUser, endTime, startTime, createExport]);

  return (
    <>
      {showExport ? (
        <NotificationLoading>
          <Notification
            closeCallBack={resetExport}
            text="Your audit log is being prepared; an email will be sent to you when the extract is ready to download."
          />
        </NotificationLoading>
      ) : null}
      {errors && <ErrorMessages errors={errors} />}
      <p>
        Export your audit log. You can select a Date range and or a specific
        user.
      </p>

      <FormControl>
        <label style={{ cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={useDateRange}
            name={`ocr`}
            onChange={() => {
              setUseDateRange((prev) => !prev);
            }}
          />
          Use Date Range
        </label>
      </FormControl>

      <FormControl>
        <label style={{ cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={filterByUser}
            name={`ocr`}
            onChange={() => {
              setFilterByUser((prev) => !prev);
              setSelectedUser();
            }}
          />
          Filter By User{selectedUser && `: ${selectedUser?.emailAddress}`}
        </label>
      </FormControl>

      {useDateRange && (
        <div style={{ display: "flex", alignItems: "center" }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DateTimePicker
              label="From"
              value={startTime}
              onChange={(newValue) => setStartTime(newValue || today)}
            />
            <div style={{ marginLeft: "1rem", marginRight: "1rem" }}>
              {" - "}
            </div>
            <DateTimePicker
              label="To"
              value={endTime}
              onChange={(newValue) => setEndTime(newValue || today)}
            />
          </LocalizationProvider>
        </div>
      )}

      {filterByUser && (
        <div style={{ marginTop: "1rem" }}>
          <SearchUsers
            selectedUser={selectedUser}
            setSelected={(user) => setSelectedUser(user)}
          />
        </div>
      )}

      <div style={{ marginTop: "1rem" }}>
        <Button
          type="button"
          list
          //   disabled={removeProviderLoading}
          danger
          onClick={() => {
            cancel();
          }}
        >
          Cancel
          {/* {removeProviderLoading ? <Spinner /> : "Yes"} */}
        </Button>
        <Button
          type="button"
          //   disabled={removeProviderLoading}
          onClick={() => {
            callCreateExport();
            trackCustomEvent(
              "export",
              "audit",
              selectedUser?.id || "unknown-id",
              "Clicked Export Audit Log"
            );
          }}
        >
          Export
        </Button>
      </div>
    </>
  );
}

export default ExportAuditLog;
